import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Save, Play, ArrowLeft, Trash2, Zap, Cog, Shuffle, Filter, Clock, GitBranch, RotateCw, ShieldAlert,
  ChevronRight, X,
} from 'lucide-react';

const ACCENT = '#f97316';

type NodeType = 'trigger' | 'action' | 'transform' | 'filter' | 'delay' | 'branch' | 'loop' | 'error_handler';

const NODE_COLORS: Record<NodeType, string> = {
  trigger: '#22c55e',
  action: '#3b82f6',
  transform: '#a855f7',
  filter: '#eab308',
  delay: '#6b7280',
  branch: '#f97316',
  loop: '#06b6d4',
  error_handler: '#ef4444',
};

const NODE_ICONS: Record<NodeType, React.ReactNode> = {
  trigger: <Zap size={14} color="#fff" />,
  action: <Cog size={14} color="#fff" />,
  transform: <Shuffle size={14} color="#fff" />,
  filter: <Filter size={14} color="#fff" />,
  delay: <Clock size={14} color="#fff" />,
  branch: <GitBranch size={14} color="#fff" />,
  loop: <RotateCw size={14} color="#fff" />,
  error_handler: <ShieldAlert size={14} color="#fff" />,
};

const NODE_LABELS: Record<NodeType, string> = {
  trigger: 'Trigger',
  action: 'Action',
  transform: 'Transform',
  filter: 'Filter',
  delay: 'Delay',
  branch: 'Branch',
  loop: 'Loop',
  error_handler: 'Error Handler',
};

interface FlowNode {
  id: string;
  type: NodeType;
  label: string;
  x: number;
  y: number;
  config: Record<string, unknown>;
  inputs: string[];
  outputs: string[];
}

interface FlowEdge {
  id: string;
  source_node_id: string;
  source_output: string;
  target_node_id: string;
  target_input: string;
  condition?: string;
}

interface Flow {
  id: string;
  name: string;
  description: string;
  status: string;
  version: number;
  trigger: { type: string; config: Record<string, unknown> };
  nodes: FlowNode[];
  edges: FlowEdge[];
  error_handling: {
    on_error: string;
    max_retries: number;
    retry_delay_seconds: number;
    notify_on_failure: boolean;
  };
  execution_count: number;
  last_executed_at: string | null;
}

const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;
const PORT_RADIUS = 6;
const MINIMAP_WIDTH = 180;
const MINIMAP_HEIGHT = 120;

export default function FlowBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);

  const [flow, setFlow] = useState<Flow | null>(null);
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [edges, setEdges] = useState<FlowEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Canvas state
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Interaction state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [draggingNode, setDraggingNode] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const [connecting, setConnecting] = useState<{ nodeId: string; output: string; startX: number; startY: number } | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Config panel
  const [configLabel, setConfigLabel] = useState('');

  const selectedNode = useMemo(() => nodes.find(n => n.id === selectedNodeId) || null, [nodes, selectedNodeId]);

  useEffect(() => {
    if (selectedNode) {
      setConfigLabel(selectedNode.label);
    }
  }, [selectedNode]);

  const loadFlow = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<{ flow: Flow }>(`/integration-flows/${id}`);
      setFlow(data.flow);
      setNodes(data.flow.nodes);
      setEdges(data.flow.edges);
    } catch {
      navigate('/flows');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { loadFlow(); }, [loadFlow]);

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;
        if (selectedNodeId) {
          setNodes(prev => prev.filter(n => n.id !== selectedNodeId));
          setEdges(prev => prev.filter(e => e.source_node_id !== selectedNodeId && e.target_node_id !== selectedNodeId));
          setSelectedNodeId(null);
        } else if (selectedEdgeId) {
          setEdges(prev => prev.filter(e => e.id !== selectedEdgeId));
          setSelectedEdgeId(null);
        }
      }
      if (e.key === 'Escape') {
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
        setConnecting(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedNodeId, selectedEdgeId]);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((screenX: number, screenY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (screenX - rect.left - pan.x) / zoom,
      y: (screenY - rect.top - pan.y) / zoom,
    };
  }, [pan, zoom]);

  // Add node from toolbar
  const addNode = (type: NodeType) => {
    const newNode: FlowNode = {
      id: crypto.randomUUID(),
      type,
      label: NODE_LABELS[type],
      x: (-pan.x + 300) / zoom,
      y: (-pan.y + 200 + nodes.length * 80) / zoom,
      config: {},
      inputs: type === 'trigger' ? [] : ['in'],
      outputs: ['out'],
    };
    if (type === 'branch') {
      newNode.outputs = ['true', 'false'];
    }
    setNodes(prev => [...prev, newNode]);
    setSelectedNodeId(newNode.id);
    setSelectedEdgeId(null);
  };

  // Mouse handlers for SVG canvas
  const handleCanvasMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const target = e.target as SVGElement;
    // If clicking on empty canvas area, start panning
    if (target === svgRef.current || target.classList.contains('canvas-bg')) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
    }
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    setMousePos({ x: e.clientX, y: e.clientY });

    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }

    if (draggingNode) {
      const pos = screenToCanvas(e.clientX, e.clientY);
      setNodes(prev => prev.map(n =>
        n.id === draggingNode.id
          ? { ...n, x: pos.x - draggingNode.offsetX, y: pos.y - draggingNode.offsetY }
          : n
      ));
    }
  };

  const handleCanvasMouseUp = () => {
    if (connecting) {
      // Check if mouse is over an input port
      const canvasPos = screenToCanvas(mousePos.x, mousePos.y);
      for (const node of nodes) {
        if (node.id === connecting.nodeId) continue;
        for (let i = 0; i < node.inputs.length; i++) {
          const portX = node.x;
          const portY = node.y + NODE_HEIGHT / 2 + (i - (node.inputs.length - 1) / 2) * 16;
          const dist = Math.sqrt((canvasPos.x - portX) ** 2 + (canvasPos.y - portY) ** 2);
          if (dist < 15) {
            // Check for duplicate edge
            const exists = edges.some(e =>
              e.source_node_id === connecting.nodeId &&
              e.source_output === connecting.output &&
              e.target_node_id === node.id &&
              e.target_input === node.inputs[i]
            );
            if (!exists) {
              const newEdge: FlowEdge = {
                id: crypto.randomUUID(),
                source_node_id: connecting.nodeId,
                source_output: connecting.output,
                target_node_id: node.id,
                target_input: node.inputs[i],
              };
              setEdges(prev => [...prev, newEdge]);
            }
            break;
          }
        }
      }
      setConnecting(null);
    }
    setIsPanning(false);
    setDraggingNode(null);
  };

  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.2, Math.min(3, zoom * delta));
    // Zoom toward cursor position
    const rect = svgRef.current?.getBoundingClientRect();
    if (rect) {
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      setPan(prev => ({
        x: mx - (mx - prev.x) * (newZoom / zoom),
        y: my - (my - prev.y) * (newZoom / zoom),
      }));
    }
    setZoom(newZoom);
  };

  // Node mouse handlers
  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    const pos = screenToCanvas(e.clientX, e.clientY);
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;
    setDraggingNode({ id: nodeId, offsetX: pos.x - node.x, offsetY: pos.y - node.y });
    setSelectedNodeId(nodeId);
    setSelectedEdgeId(null);
  };

  // Output port mouse handlers
  const handleOutputPortMouseDown = (e: React.MouseEvent, nodeId: string, output: string, portX: number, portY: number) => {
    e.stopPropagation();
    setConnecting({ nodeId, output, startX: portX, startY: portY });
  };

  // Edge click
  const handleEdgeClick = (e: React.MouseEvent, edgeId: string) => {
    e.stopPropagation();
    setSelectedEdgeId(edgeId);
    setSelectedNodeId(null);
  };

  // Save
  const handleSave = async () => {
    if (!flow) return;
    setSaving(true);
    try {
      await api.patch(`/integration-flows/${flow.id}`, { nodes, edges });
      const data = await api.get<{ flow: Flow }>(`/integration-flows/${flow.id}`);
      setFlow(data.flow);
    } catch (err: any) {
      alert(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  // Execute
  const handleExecute = async () => {
    if (!flow) return;
    setExecuting(true);
    try {
      await api.post(`/integration-flows/${flow.id}/execute`);
      // Reload to get updated execution count
      const data = await api.get<{ flow: Flow }>(`/integration-flows/${flow.id}`);
      setFlow(data.flow);
    } catch (err: any) {
      alert(err.message || 'Failed to execute');
    }
    setExecuting(false);
  };

  // Update config label
  const handleLabelChange = (label: string) => {
    setConfigLabel(label);
    if (selectedNodeId) {
      setNodes(prev => prev.map(n => n.id === selectedNodeId ? { ...n, label } : n));
    }
  };

  // Get port positions
  const getOutputPortPos = (node: FlowNode, outputIndex: number) => {
    const total = node.outputs.length;
    return {
      x: node.x + NODE_WIDTH,
      y: node.y + NODE_HEIGHT / 2 + (outputIndex - (total - 1) / 2) * 16,
    };
  };

  const getInputPortPos = (node: FlowNode, inputIndex: number) => {
    const total = node.inputs.length;
    return {
      x: node.x,
      y: node.y + NODE_HEIGHT / 2 + (inputIndex - (total - 1) / 2) * 16,
    };
  };

  // Render cubic bezier edge
  const renderEdge = (edge: FlowEdge) => {
    const sourceNode = nodes.find(n => n.id === edge.source_node_id);
    const targetNode = nodes.find(n => n.id === edge.target_node_id);
    if (!sourceNode || !targetNode) return null;

    const sourceOutputIdx = sourceNode.outputs.indexOf(edge.source_output);
    const targetInputIdx = targetNode.inputs.indexOf(edge.target_input);
    if (sourceOutputIdx === -1 || targetInputIdx === -1) return null;

    const start = getOutputPortPos(sourceNode, sourceOutputIdx);
    const end = getInputPortPos(targetNode, targetInputIdx);
    const dx = Math.abs(end.x - start.x) * 0.5;
    const path = `M ${start.x} ${start.y} C ${start.x + dx} ${start.y}, ${end.x - dx} ${end.y}, ${end.x} ${end.y}`;
    const isSelected = selectedEdgeId === edge.id;

    return (
      <g key={edge.id}>
        {/* Hit area */}
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={12}
          style={{ cursor: 'pointer' }}
          onClick={(e) => handleEdgeClick(e, edge.id)}
        />
        <path
          d={path}
          fill="none"
          stroke={isSelected ? ACCENT : '#94a3b8'}
          strokeWidth={isSelected ? 2.5 : 2}
          markerEnd="url(#arrowhead)"
          style={{ pointerEvents: 'none' }}
        />
        {edge.condition && (
          <text
            x={(start.x + end.x) / 2}
            y={(start.y + end.y) / 2 - 8}
            textAnchor="middle"
            fontSize={10}
            fill="#6b7280"
          >{edge.condition}</text>
        )}
      </g>
    );
  };

  // Render connecting line
  const renderConnectingLine = () => {
    if (!connecting) return null;
    const canvasPos = screenToCanvas(mousePos.x, mousePos.y);
    const dx = Math.abs(canvasPos.x - connecting.startX) * 0.5;
    const path = `M ${connecting.startX} ${connecting.startY} C ${connecting.startX + dx} ${connecting.startY}, ${canvasPos.x - dx} ${canvasPos.y}, ${canvasPos.x} ${canvasPos.y}`;
    return <path d={path} fill="none" stroke={ACCENT} strokeWidth={2} strokeDasharray="6 3" />;
  };

  // Render node
  const renderNode = (node: FlowNode) => {
    const color = NODE_COLORS[node.type];
    const isSelected = selectedNodeId === node.id;

    return (
      <g key={node.id}>
        {/* Node body */}
        <rect
          x={node.x}
          y={node.y}
          width={NODE_WIDTH}
          height={NODE_HEIGHT}
          rx={8}
          fill="#fff"
          stroke={isSelected ? ACCENT : '#e5e7eb'}
          strokeWidth={isSelected ? 2.5 : 1}
          filter="url(#shadow)"
          style={{ cursor: draggingNode?.id === node.id ? 'grabbing' : 'grab' }}
          onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
        />
        {/* Color strip on left */}
        <rect
          x={node.x}
          y={node.y}
          width={6}
          height={NODE_HEIGHT}
          rx={8}
          fill={color}
          style={{ pointerEvents: 'none' }}
        />
        <clipPath id={`clip-strip-${node.id}`}>
          <rect x={node.x} y={node.y} width={6} height={NODE_HEIGHT} rx={8} />
        </clipPath>
        <rect
          x={node.x}
          y={node.y}
          width={6}
          height={NODE_HEIGHT}
          fill={color}
          clipPath={`url(#clip-strip-${node.id})`}
          style={{ pointerEvents: 'none' }}
        />
        {/* Icon */}
        <foreignObject x={node.x + 14} y={node.y + NODE_HEIGHT / 2 - 10} width={20} height={20} style={{ pointerEvents: 'none' }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {NODE_ICONS[node.type]}
          </div>
        </foreignObject>
        {/* Label */}
        <text
          x={node.x + 42}
          y={node.y + 24}
          fontSize={12}
          fontWeight={600}
          fill="#111827"
          style={{ pointerEvents: 'none' }}
        >{node.label.length > 16 ? node.label.slice(0, 16) + '...' : node.label}</text>
        {/* Type label */}
        <text
          x={node.x + 42}
          y={node.y + 42}
          fontSize={10}
          fill="#9ca3af"
          style={{ pointerEvents: 'none' }}
        >{NODE_LABELS[node.type]}</text>

        {/* Input ports */}
        {node.inputs.map((input, i) => {
          const pos = getInputPortPos(node, i);
          return (
            <g key={`in-${input}`}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r={PORT_RADIUS}
                fill="#fff"
                stroke="#94a3b8"
                strokeWidth={1.5}
                style={{ cursor: 'crosshair' }}
              />
              {node.inputs.length > 1 && (
                <text x={pos.x - 12} y={pos.y + 3} fontSize={8} fill="#9ca3af" textAnchor="end">{input}</text>
              )}
            </g>
          );
        })}

        {/* Output ports */}
        {node.outputs.map((output, i) => {
          const pos = getOutputPortPos(node, i);
          return (
            <g key={`out-${output}`}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r={PORT_RADIUS}
                fill="#fff"
                stroke={NODE_COLORS[node.type]}
                strokeWidth={1.5}
                style={{ cursor: 'crosshair' }}
                onMouseDown={(e) => handleOutputPortMouseDown(e, node.id, output, pos.x, pos.y)}
              />
              {node.outputs.length > 1 && (
                <text x={pos.x + 12} y={pos.y + 3} fontSize={8} fill="#9ca3af">{output}</text>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  // Minimap
  const renderMinimap = () => {
    if (nodes.length === 0) return null;
    const allX = nodes.map(n => n.x);
    const allY = nodes.map(n => n.y);
    const minX = Math.min(...allX) - 50;
    const minY = Math.min(...allY) - 50;
    const maxX = Math.max(...allX) + NODE_WIDTH + 50;
    const maxY = Math.max(...allY) + NODE_HEIGHT + 50;
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const scale = Math.min(MINIMAP_WIDTH / rangeX, MINIMAP_HEIGHT / rangeY);

    // Viewport indicator
    const rect = svgRef.current?.getBoundingClientRect();
    const vw = rect ? rect.width : 800;
    const vh = rect ? rect.height : 600;
    const vpLeft = (-pan.x / zoom - minX) * scale;
    const vpTop = (-pan.y / zoom - minY) * scale;
    const vpWidth = (vw / zoom) * scale;
    const vpHeight = (vh / zoom) * scale;

    return (
      <div style={{
        position: 'absolute', bottom: 12, right: 12,
        width: MINIMAP_WIDTH, height: MINIMAP_HEIGHT,
        backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb',
        borderRadius: '0.375rem', overflow: 'hidden', pointerEvents: 'none',
      }}>
        <svg width={MINIMAP_WIDTH} height={MINIMAP_HEIGHT}>
          {/* Viewport rect */}
          <rect x={vpLeft} y={vpTop} width={vpWidth} height={vpHeight} fill="rgba(249,115,22,0.1)" stroke={ACCENT} strokeWidth={1} />
          {/* Nodes */}
          {nodes.map(n => (
            <rect
              key={n.id}
              x={(n.x - minX) * scale}
              y={(n.y - minY) * scale}
              width={NODE_WIDTH * scale}
              height={NODE_HEIGHT * scale}
              fill={NODE_COLORS[n.type]}
              rx={2}
            />
          ))}
          {/* Edges */}
          {edges.map(edge => {
            const src = nodes.find(n => n.id === edge.source_node_id);
            const tgt = nodes.find(n => n.id === edge.target_node_id);
            if (!src || !tgt) return null;
            return (
              <line
                key={edge.id}
                x1={(src.x + NODE_WIDTH - minX) * scale}
                y1={(src.y + NODE_HEIGHT / 2 - minY) * scale}
                x2={(tgt.x - minX) * scale}
                y2={(tgt.y + NODE_HEIGHT / 2 - minY) * scale}
                stroke="#94a3b8"
                strokeWidth={0.5}
              />
            );
          })}
        </svg>
      </div>
    );
  };

  if (loading) return <p style={{ color: '#6b7280', padding: '2rem' }}>Loading flow...</p>;
  if (!flow) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px - 3rem)', margin: '-1.5rem', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.625rem 1rem', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => navigate('/flows')} style={{ ...iconBtnStyle, border: 'none' }} title="Back to flows">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>{flow.name}</h1>
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>v{flow.version} &middot; {nodes.length} nodes &middot; {edges.length} edges</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{flow.execution_count} runs</span>
          <button onClick={handleSave} disabled={saving} style={headerBtnStyle} title="Save">
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={handleExecute} disabled={executing} style={{ ...headerBtnStyle, backgroundColor: '#22c55e' }} title="Execute">
            <Play size={14} /> {executing ? 'Running...' : 'Run'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Toolbar */}
        <div style={{
          width: 56, backgroundColor: '#111827', display: 'flex', flexDirection: 'column',
          alignItems: 'center', padding: '0.5rem 0', gap: '0.25rem', flexShrink: 0,
        }}>
          {(Object.keys(NODE_COLORS) as NodeType[]).map(type => (
            <button
              key={type}
              onClick={() => addNode(type)}
              title={`Add ${NODE_LABELS[type]}`}
              style={{
                width: 40, height: 40, borderRadius: '0.375rem', border: 'none',
                backgroundColor: 'transparent', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1f2937')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%', backgroundColor: NODE_COLORS[type],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {NODE_ICONS[type]}
              </div>
              <span style={{ fontSize: '0.5rem', color: '#9ca3af', lineHeight: 1 }}>{NODE_LABELS[type].slice(0, 6)}</span>
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, position: 'relative', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            style={{ cursor: isPanning ? 'grabbing' : (connecting ? 'crosshair' : 'default') }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseUp}
            onWheel={handleWheel}
          >
            <defs>
              <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <path d="M 0 0 L 8 3 L 0 6 Z" fill="#94a3b8" />
              </marker>
              <filter id="shadow" x="-10%" y="-10%" width="130%" height="140%">
                <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.08" />
              </filter>
              {/* Grid pattern */}
              <pattern id="grid" width={20 * zoom} height={20 * zoom} patternUnits="userSpaceOnUse"
                x={pan.x % (20 * zoom)} y={pan.y % (20 * zoom)}>
                <circle cx={1} cy={1} r={0.5} fill="#d1d5db" />
              </pattern>
            </defs>

            {/* Grid background */}
            <rect width="100%" height="100%" fill="url(#grid)" className="canvas-bg" />

            {/* Transform group for pan/zoom */}
            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* Edges */}
              {edges.map(renderEdge)}
              {/* Connecting line */}
              {renderConnectingLine()}
              {/* Nodes */}
              {nodes.map(renderNode)}
            </g>
          </svg>

          {/* Minimap */}
          {renderMinimap()}

          {/* Zoom indicator */}
          <div style={{
            position: 'absolute', bottom: 12, left: 12, fontSize: '0.6875rem',
            color: '#9ca3af', backgroundColor: 'rgba(255,255,255,0.9)', padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem', border: '1px solid #e5e7eb',
          }}>
            {Math.round(zoom * 100)}%
          </div>

          {/* Empty state hint */}
          {nodes.length === 0 && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              textAlign: 'center', color: '#9ca3af', pointerEvents: 'none',
            }}>
              <GitBranch size={40} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>Click a node type in the left toolbar to start building your flow</p>
            </div>
          )}
        </div>

        {/* Right Config Panel */}
        {selectedNode && (
          <div style={{
            width: 280, backgroundColor: '#fff', borderLeft: '1px solid #e5e7eb',
            overflow: 'auto', flexShrink: 0,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', backgroundColor: NODE_COLORS[selectedNode.type],
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {NODE_ICONS[selectedNode.type]}
                </div>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{NODE_LABELS[selectedNode.type]}</span>
              </div>
              <button onClick={() => setSelectedNodeId(null)} style={{ ...iconBtnSmall, border: 'none' }}>
                <X size={14} />
              </button>
            </div>

            <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={panelLabelStyle}>Label</label>
                <input value={configLabel} onChange={e => handleLabelChange(e.target.value)} style={panelInputStyle} />
              </div>
              <div>
                <label style={panelLabelStyle}>Node ID</label>
                <p style={{ fontFamily: 'monospace', fontSize: '0.6875rem', color: '#9ca3af', margin: 0, wordBreak: 'break-all' }}>{selectedNode.id}</p>
              </div>
              <div>
                <label style={panelLabelStyle}>Position</label>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>x: {Math.round(selectedNode.x)}, y: {Math.round(selectedNode.y)}</p>
              </div>
              <div>
                <label style={panelLabelStyle}>Inputs</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {selectedNode.inputs.length === 0 ? (
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>None (trigger node)</span>
                  ) : selectedNode.inputs.map(input => (
                    <span key={input} style={{ padding: '0.125rem 0.375rem', fontSize: '0.6875rem', backgroundColor: '#f3f4f6', borderRadius: '0.25rem', color: '#6b7280' }}>{input}</span>
                  ))}
                </div>
              </div>
              <div>
                <label style={panelLabelStyle}>Outputs</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {selectedNode.outputs.map(output => (
                    <span key={output} style={{ padding: '0.125rem 0.375rem', fontSize: '0.6875rem', backgroundColor: '#f3f4f6', borderRadius: '0.25rem', color: '#6b7280' }}>{output}</span>
                  ))}
                </div>
              </div>

              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                <label style={panelLabelStyle}>Connected Edges</label>
                {edges.filter(e => e.source_node_id === selectedNode.id || e.target_node_id === selectedNode.id).map(edge => {
                  const otherNodeId = edge.source_node_id === selectedNode.id ? edge.target_node_id : edge.source_node_id;
                  const otherNode = nodes.find(n => n.id === otherNodeId);
                  const direction = edge.source_node_id === selectedNode.id ? 'out' : 'in';
                  return (
                    <div key={edge.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      <ChevronRight size={12} style={{ transform: direction === 'in' ? 'rotate(180deg)' : undefined }} />
                      <span>{direction === 'out' ? edge.source_output : edge.target_input}</span>
                      <span style={{ color: '#d1d5db' }}>&rarr;</span>
                      <span style={{ fontWeight: 500 }}>{otherNode?.label || 'Unknown'}</span>
                    </div>
                  );
                })}
                {edges.filter(e => e.source_node_id === selectedNode.id || e.target_node_id === selectedNode.id).length === 0 && (
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>No connections</p>
                )}
              </div>

              <button
                onClick={() => {
                  setNodes(prev => prev.filter(n => n.id !== selectedNode.id));
                  setEdges(prev => prev.filter(e => e.source_node_id !== selectedNode.id && e.target_node_id !== selectedNode.id));
                  setSelectedNodeId(null);
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                  padding: '0.5rem', fontSize: '0.8125rem', color: '#dc2626', backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca', borderRadius: '0.375rem', cursor: 'pointer', marginTop: '0.5rem',
                }}
              >
                <Trash2 size={14} /> Delete Node
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '2rem', height: '2rem', backgroundColor: 'transparent',
  border: '1px solid #e5e7eb', borderRadius: '0.375rem', cursor: 'pointer', color: '#6b7280',
};

const iconBtnSmall: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '1.5rem', height: '1.5rem', backgroundColor: 'transparent',
  border: '1px solid #e5e7eb', borderRadius: '0.25rem', cursor: 'pointer', color: '#9ca3af',
};

const headerBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
  padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 500,
  backgroundColor: ACCENT, color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
};

const panelLabelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: '#9ca3af',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem',
};

const panelInputStyle: React.CSSProperties = {
  width: '100%', padding: '0.375rem 0.5rem', fontSize: '0.8125rem',
  border: '1px solid #e5e7eb', borderRadius: '0.25rem', boxSizing: 'border-box',
};
